import Model from 'src/app/common/model';

/**
 * @model CustomerLeadModel
 * @description
 * Represents a Customer Lead within the AquaCloud Trading Community.
 * A "Lead" acts as a precursor to a transaction or a business relationship. It captures
 * an expression of interest from a user (Farmer, Hatchery, Feed Provider) regarding
 * buying or selling aquaculture products.
 *
 * **Domain Context:**
 * In the aquaculture ecosystem, timely information about seed availability (PLs),
 * harvest readiness, or feed requirements is crucial. This model standardizes how
 * these "signals" are stored and processed before they become formal orders or contracts.
 *
 * **Purpose:**
 * To serve as a Data Transfer Object (DTO) for lead management operations.
 * It encapsulates:
 * - **Who**: The contact details (`mobileNumber`) and creator (`createdBy`).
 * - **What**: The specific product details (`lead_culture`, `size_weight`, `available_stage`).
 * - **When**: Timestamps for generation and status updates.
 * - **Status**: The current state of the lead (Active, Converted, Closed).
 *
 * **Key Responsibilities:**
 * 1. **Inquiry Capture**: Records raw details of what the customer is looking for.
 * 2. **Status Tracking**: Monitors the progress of the lead via `status` and `converted` flags.
 * 3. **Metric Standardization**: Stores biological metrics (weight, length, count) with their UOMs.
 * 4. **Source Attribution**: Identifies where the lead originated (`lead_widget`, `lead_from`).
 *
 * **Data Flow:**
 * - **Creation**: Generated via the "Marketplace" or "Seed Availability" widgets in the UI.
 * - **Processing**: Reviewed by sales teams or matched automatically with providers.
 * - **Conversion**: If successful, the lead is marked `converted: true` and may spawn a `Transaction`.
 *
 * **User Roles & Permissions:**
 * - **Farmers**: Create leads to buy seeds or sell harvest.
 * - **Providers**: View leads relevant to their business type.
 * - **System Admin**: Full access to manage lead lifecycle.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CustomerLeadModel extends Model {
  /**
   * @type {object}
   * @description Generic container for additional unstructured details about the lead.
   * @optional
   */
  leadDetails: object;

  /**
   * @type {object}
   * @description Configuration options or preferences associated with the lead.
   * @optional
   */
  options: object;

  /**
   * @type {any}
   * @description The timestamp when the lead was generated.
   * Typically an ISO 8601 date string.
   */
  generatedDate: any;

  /**
   * @type {string}
   * @description The specific event or trigger that created this lead.
   * @optional
   */
  event: string;

  /**
   * @type {string}
   * @description Context indicating the source entity or screen details.
   * @optional
   */
  details_from: string;

  /**
   * @type {string}
   * @description Context indicating the target entity or screen details.
   * @optional
   */
  details_to: string;

  /**
   * @type {boolean}
   * @description Flag indicating if the lead is currently active and actionable.
   */
  isActive: boolean;

  /**
   * @type {string}
   * @description The current status code or label of the lead (e.g., 'New', 'Contacted', 'Closed').
   */
  status: string;

  /**
   * @type {any}
   * @description The timestamp of the last status change.
   * @optional
   */
  status_date: any;

  /**
   * @type {string}
   * @description The unique identifier of the user who created the lead.
   */
  createdBy: string;

  /**
   * @type {string}
   * @description The unique identifier of the user who last updated the lead.
   */
  updatedBy: string;

  /**
   * @type {string}
   * @description The primary mobile contact number for the lead.
   */
  mobileNumber: string;

  /**
   * @type {number}
   * @description One-Time Password used for verification during lead creation (if applicable).
   * @optional
   */
  otp: number;

  /**
   * @type {string}
   * @description ID linking to a specific Seed Availability record, if this lead is about seeds.
   * @optional
   */
  seedAvailabilityId: string;

  /**
   * @type {{ name: string; id: number }}
   * @description The specific culture species or type associated with the lead.
   * @example { name: "Vannamei", id: 1 }
   */
  lead_culture: { name: string; id: number };

  /**
   * @type {string}
   * @description The UI widget or component name where the lead originated.
   * @optional
   */
  lead_widget: string;

  /**
   * @type {string}
   * @description The biological stage of the product (e.g., "PL10", "Juvenile").
   * @optional
   */
  available_stage: string;

  /**
   * @type {number}
   * @description The weight metric of the product.
   * @optional
   */
  size_weight: number;

  /**
   * @type {string}
   * @description Unit of Measure for weight (e.g., "gms", "kg").
   * @optional
   */
  size_weight_uom: string;

  /**
   * @type {number}
   * @description The length metric of the product.
   * @optional
   */
  size_length: number;

  /**
   * @type {string}
   * @description Unit of Measure for length (e.g., "cm", "inch").
   * @optional
   */
  size_length_uom: string;

  /**
   * @type {number}
   * @description The count metric (e.g., count per kg).
   * @optional
   */
  size_count: number;

  /**
   * @type {string}
   * @description Unit of Measure for count.
   * @optional
   */
  size_count_uom: string;

  /**
   * @type {string}
   * @description Describes the origin source of the lead (e.g., "Mobile App", "Web Portal").
   * @optional
   */
  lead_from: string;

  /**
   * @type {boolean}
   * @description Flag indicating if the lead has been successfully converted into a transaction.
   */
  converted: boolean;

  /**
   * @type {string}
   * @description External reference ID or code for the lead.
   * @optional
   */
  lead_reference: string

  /**
   * @type {any}
   * @description The type of business associated with the lead (e.g., Farm, Hatchery).
   * @optional
   */
  businessType: any;

  /**
   * @type {String}
   * @description International dialing code for the mobile number.
   */
  dialingCode: String;

  /**
   * @type {String}
   * @description ISO country code associated with the contact number.
   */
  countryCode: String;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the CustomerLeadModel.
   * Calls the parent `Model` constructor.
   */
  constructor() {
    super();
  }
}

/**
 * Represents the Excel export object structure for customer leads.
 */
export class LeadsExcelObj {
  report?: string;
  businessType?: string;
  cultureCategory?: any;
  date?: any;
  login?: { role: string; businessName: string; location: string; mobileNumber: string };
  generatedTime?: string;
  status?: string[];
  hatcheryReport?: boolean;

  constructor(init?: Partial<LeadsExcelObj>) {
    if (init) Object.assign(this, init);
  }
}
